import { useState, useEffect, useRef } from "react";
import { BACKEND_API_URL } from "@/constants/api";
import { getIdToken } from "@/utils/authUtils";
import PracticeSessionStatsService from "@/services/PracticeSessionStatService";
import useStore from "@/store";
import { toast } from "sonner";

export const usePhonemeRecognition = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [phonemes, setPhonemes] = useState([]);
  const [overallAccuracy, setOverallAccuracy] = useState(0);
  const [adjustedWpm, setAdjustedWpm] = useState(0);
  const [fullRepeatCount, setFullRepeatCount] = useState(0);
  const [error, setError] = useState("");
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isPredicting, setIsPredicting] = useState(false);
  const [directMatches, setDirectMatches] = useState(0);

  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const streamRef = useRef(null);

  const { user } = useStore().user;
  const { currentContent } = useStore().collections;

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  const cancelPhonemeRecognition = () => {
    setIsRecording(false);
    setPhonemes([]);
    setElapsedTime(0);
    setError("");
    recordedChunksRef.current = [];
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    }
  };

  const startPhonemeRecognition = async (sentence, language, repeatable, save = true) => {
    setIsRecording(true);
    setPhonemes([]);
    setError("");
    recordedChunksRef.current = [];

    let startTime;
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(streamRef.current);

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      mediaRecorderRef.current.onstart = () => {
        startTime = Date.now();
      };

      mediaRecorderRef.current.onstop = async () => {
        const endTime = Date.now();
        setElapsedTime((endTime - startTime) / 1000);

        const audioBlob = new Blob(recordedChunksRef.current, { type: "audio/wav" });
        const formData = new FormData();
        formData.append("file", audioBlob, "recording.wav");
        formData.append("source_text", sentence);
        formData.append("language", language);
        formData.append("repeatable", repeatable);

        const token = await getIdToken();
        setIsPredicting(true);

        const response = await fetch(`${BACKEND_API_URL}/transcribe`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        setIsPredicting(false);

        // ── NEW LOGGING SNIPPET ──
        if (!response.ok) {
          let details = "No details";
          try {
            const errJson = await response.json();
            details = errJson.details || errJson.error || JSON.stringify(errJson);
          } catch (e) {
            console.error("Failed to parse error JSON", e);
          }
          console.error("Transcribe API error:", response.status, details);
          throw new Error(`Transcribe failed: ${details}`);
        }
        // ── end logging snippet ──

        // Parse and set your data
        const data = await response.json();
        setPhonemes(data.segments || []);
        setOverallAccuracy(data.overall_accuracy || 0);
        setFullRepeatCount(data.full_repeat_count || 0);
        setDirectMatches(data.direct_matches || 0);
        console.log('directMatches', data.direct_matches);

        const accuracyRate = data.overall_accuracy / 100;
        const rawWpm = data.segments.length / ((endTime - startTime) / 60000);
        setAdjustedWpm(rawWpm * accuracyRate);

        if (save) {
          PracticeSessionStatsService.createPracticeSessionStat(
            currentContent.id,
            user.current_collection_id,
            language,
            rawWpm * accuracyRate,
            data.overall_accuracy,
            data.segments.length,
            data.segments.map((p) => p.text),
            data.segments,
            [],
            (endTime - startTime) / 1000
          );
        }
      };

      mediaRecorderRef.current.start();
    } catch (err) {
      setError("Could not start recording: " + err.message);
      setIsRecording(false);
    }
  };

  const stopPhonemeRecognition = () => {
    setPhonemes([]);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    }
  };

  return {
    isRecording,
    phonemes,
    directMatches,
    overallAccuracy,
    adjustedWpm,
    elapsedTime,
    fullRepeatCount,
    error,
    isPredicting,
    startPhonemeRecognition,
    stopPhonemeRecognition,
    cancelPhonemeRecognition,
  };
};
